import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftIcon, PlayIcon } from "@heroicons/react/24/outline";
import { PageLoader, Button } from "../../ui";
import VaptStatusBadge from "../components/VaptStatusBadge";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import VaptTable from "../components/VaptTable";
import {
  getVaptScanApi,
  listVaptToolExecutionsApi,
  listVaptFindingsApi,
  launchVaptScanApi,
} from "../../../services/axios/AuthApis/VAPTApis";

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

// Not enumerated as a top-level page in the original spec (which listed
// VaptScans.js / VaptCreateScan.js / VaptScanWorkers.js), but the required
// `/scans/:id` route needs a detail view, so this small page was added to
// satisfy that route cleanly.
const VaptScanDetail = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [scan, setScan] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [launching, setLaunching] = useState(false);
  // Surfaces a launch failure passed by VaptCreateScan.js's create-then-
  // launch flow (e.g. "targets not authorized/approved") so the user sees
  // WHY the scan they just created is still sitting at Queued instead of it
  // looking like a silent no-op. Kept separate from `error` (the page-load
  // error) since `load()` resets `error` on every fetch.
  const [launchNotice, setLaunchNotice] = useState(location.state?.launchError || null);

  const load = () => {
    setLoading(true);
    setError(null);
    getVaptScanApi(scanId)
      .then(async (data) => {
        setScan(data);
        // Server-side scan_id filter (VAPTFindingViewSet ?scan_id=) - the
        // previous client-side filter compared f.scan (the finding's raw
        // Mongo ObjectId reference to its scan) against scanId/data.scan_id
        // (the human-readable "SCAN-XXXX" business key), which could never
        // match, so this panel always rendered empty even when the scan
        // had real findings.
        const [exec, findingsRes] = await Promise.all([
          listVaptToolExecutionsApi(scanId),
          listVaptFindingsApi({ scan_id: scanId }),
        ]);
        setExecutions(asArray(exec));
        setFindings(asArray(findingsRes).map((f) => ({ ...f, id: f.finding_id })));
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load scan.");
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, [scanId]); // eslint-disable-line

  // Launch is server-gated: the backend returns a 400 with
  // "Scan cannot be launched: one or more targets are not authorized/
  // approved." if any external-sourced target lacks an Approved
  // Authorization. Surface that message verbatim instead of a generic one.
  const handleLaunch = async () => {
    setLaunching(true);
    setError(null);
    try {
      await launchVaptScanApi(scanId);
      setLaunchNotice(null);
      load();
      pollToolExecutions();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Failed to launch scan.");
    } finally {
      setLaunching(false);
    }
  };

  // Real orchestration is a Celery stub (vapt/tasks.py) — no actual tool
  // runs. This polls the real, read-only tool-executions endpoint (the only
  // source of truth for execution status) every ~3s so the page reflects
  // whatever the backend orchestration produces, without ever fabricating
  // progress client-side. Stops once every execution reaches a terminal
  // state or after a bounded number of polls.
  const pollToolExecutions = () => {
    let polls = 0;
    const maxPolls = 10;
    const interval = setInterval(async () => {
      polls += 1;
      try {
        const latest = await listVaptToolExecutionsApi(scanId);
        load();
        const rows = asArray(latest);
        const allTerminal = rows.length > 0 && rows.every((e) => ["Completed", "Failed", "Skipped", "Cancelled", "Not Applicable"].includes(e.status));
        if (allTerminal || polls >= maxPolls) clearInterval(interval);
      } catch {
        clearInterval(interval);
      }
    }, 3000);
  };

  if (loading) return <PageLoader title="Loading scan…" />;
  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }
  if (!scan) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Scan not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="w-4 h-4" /> Back
      </button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-lg font-bold text-gray-900">{scan.scan_name || scan.scan_id}</h2>
            <VaptStatusBadge status={scan.status} />
          </div>
          <p className="text-sm text-gray-500">{scan.project_name || scan.project_display_id || "Asset VAPT Project"}</p>
        </div>
        {launchNotice && (
          <div className="w-full bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-2.5">
            {launchNotice}
          </div>
        )}
        {scan.status === "Draft" || scan.status === "Queued" ? (
          <Button icon={PlayIcon} loading={launching} onClick={handleLaunch}>
            Launch Scan
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-400 uppercase">Scan Type</p>
          <p className="text-sm font-semibold text-gray-800">{scan.scan_type || "-"}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-400 uppercase">Target</p>
          <p className="text-sm font-semibold text-gray-800">{scan.target_display ?? scan.target_name ?? "-"}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-400 uppercase">Start Time</p>
          <p className="text-sm font-semibold text-gray-800">{scan.start_time ? new Date(scan.start_time).toLocaleString() : "-"}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-400 uppercase">End Time</p>
          <p className="text-sm font-semibold text-gray-800">{scan.end_time ? new Date(scan.end_time).toLocaleString() : "-"}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-400 uppercase">Findings</p>
          <p className="text-sm font-semibold text-gray-800">{scan.vulnerabilities_found ?? "-"}</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-xs text-gray-400 uppercase mb-2">Tools Configured</p>
        {scan.configured_tools && scan.configured_tools.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {scan.configured_tools.map((t) => (
              <span key={t} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                {t}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No tools configured for this scan.</p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-xs text-gray-400 uppercase mb-2">Execution</p>
        {executions.length > 0 ? (
          <VaptTable
            columns={[
              { key: "tool_id", header: "Tool" },
              { key: "status", header: "Status", render: (r) => <VaptStatusBadge status={r.status} size="sm" /> },
              { key: "started_at", header: "Started", render: (r) => (r.started_at ? new Date(r.started_at).toLocaleString() : "-") },
              { key: "completed_at", header: "Completed", render: (r) => (r.completed_at ? new Date(r.completed_at).toLocaleString() : "-") },
              { key: "error_message", header: "Error", render: (r) => r.error_message || "-" },
            ]}
            rows={executions.map((e) => ({ ...e, id: e.execution_id }))}
          />
        ) : (
          /*
            No ToolExecution rows exist yet for this scan (the scan has not
            been launched, or launch/orchestration has not produced any).
            Do not infer a "Completed" status for a tool just because it was
            configured — GET /api/vapt/tool-executions/ is the only source of
            truth for whether a tool actually ran.
          */
          <p className="text-sm text-gray-400">
            No tool execution has been recorded for this scan. Launch the scan to begin execution.
          </p>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Findings from this scan</h3>
        <VaptTable
          columns={[
            { key: "title", header: "Finding" },
            { key: "severity", header: "Severity", render: (r) => <VaptSeverityBadge severity={r.severity} size="sm" /> },
            { key: "target_display", header: "Target", render: (r) => r.target_display ?? "—" },
            { key: "finding_status", header: "Status", render: (r) => <VaptStatusBadge status={r.finding_status} size="sm" /> },
          ]}
          rows={findings}
          onRowClick={(row) => navigate(`/ai-vapt/findings/${row.finding_id}`)}
          emptyMessage="No findings have been recorded from this scan."
        />
      </div>
    </div>
  );
};

export default VaptScanDetail;
